import { authApi } from '../api/authApi';
import { LoginRequest, LoginResponse } from '../api/models/ApiAuth';

interface AuthUser {
  id: string;
  roles: string[];
  lang: string;
  region: string;
}

export class AuthService {
  private static instance: AuthService;
  private currentUser: AuthUser | null = null;
  private authChangeListeners: Array<(user: AuthUser | null) => void> = [];

  private constructor() {
    this.initializeAuth();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Đăng nhập
   */
  async login(loginData: LoginRequest): Promise<AuthUser> {
    try {
      const response: LoginResponse = await authApi.login(loginData);
      
      // Cập nhật current user trước
      this.currentUser = response.user;
      
      // Lưu tokens và user info vào localStorage
      this.saveTokens(response.accessToken, response.refreshToken, response.accessTokenExpiresAt, response.refreshTokenExpiresAt);
      
      // Thông báo thay đổi auth state
      this.notifyAuthChange();
      
      return response.user;
    } catch (error: any) {
      // Xử lý lỗi cụ thể theo tài liệu API
      if (error.response?.status === 400) {
        // Validation error
        const messages = error.response.data?.messages || ['Invalid input data'];
        throw new Error(messages.join(', '));
      } else if (error.response?.status === 404) {
        // Invalid email or password
        throw new Error('Invalid email or password');
      } else if (error.response?.status === 403) {
        // Account not activated
        throw new Error('Account is not activated');
      } else {
        throw new Error('An unexpected error occurred. Please try again later.');
      }
    }
  }

  /**
   * Đăng xuất
   */
  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    
    try {
      if (refreshToken) {
        await authApi.logout({ refreshToken });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Xóa tokens và user info
      this.clearTokens();
      this.currentUser = null;
      this.notifyAuthChange();
    }
  }

  /**
   * Kiểm tra xem user đã đăng nhập chưa
   */
  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    
    // Nếu không có token, chắc chắn chưa đăng nhập
    if (!accessToken) {
      return false;
    }
    
    // Nếu token đã hết hạn, chưa đăng nhập
    if (this.isAccessTokenExpired()) {
      return false;
    }
    
    // Nếu có token hợp lệ nhưng chưa có currentUser, thử khôi phục từ localStorage
    if (!this.currentUser) {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        try {
          this.currentUser = JSON.parse(userInfo);
        } catch (error) {
          console.error('Failed to parse user info:', error);
          return false;
        }
      } else {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Kiểm tra xem user có role admin không
   */
  isAdmin(): boolean {
    // Đảm bảo user đã đăng nhập trước khi kiểm tra role
    if (!this.isAuthenticated()) {
      return false;
    }
    
    return this.currentUser?.roles.includes('admin') || false;
  }

  /**
   * Lấy thông tin user hiện tại
   */
  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  /**
   * Lấy access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  /**
   * Lấy refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Kiểm tra xem access token có hết hạn chưa
   */
  isAccessTokenExpired(): boolean {
    const expiresAt = localStorage.getItem('accessTokenExpiresAt');
    if (!expiresAt) return true;
    
    const expirationTime = new Date(expiresAt).getTime();
    const currentTime = new Date().getTime();
    
    return currentTime >= expirationTime;
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await authApi.refreshToken({ refreshToken });
      
      // Cập nhật tokens (user info sẽ được giữ nguyên)
      this.saveTokens(response.accessToken, response.refreshToken, response.accessTokenExpiresAt, response.refreshTokenExpiresAt);
      
      return response.accessToken;
    } catch (error) {
      // Refresh token không hợp lệ, đăng xuất
      console.error('Failed to refresh token:', error);
      this.logout();
      return null;
    }
  }

  /**
   * Subscribe để lắng nghe thay đổi auth state
   */
  onAuthChange(callback: (user: AuthUser | null) => void): () => void {
    this.authChangeListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.authChangeListeners = this.authChangeListeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Khởi tạo auth state từ stored tokens
   */
  private initializeAuth(): void {
    console.log('Initializing auth state...');
    const accessToken = this.getAccessToken();
    
    if (accessToken && !this.isAccessTokenExpired()) {
      console.log('Found valid access token, restoring user info...');
      // Trong thực tế, bạn có thể muốn verify token hoặc lấy user info từ API
      // Ở đây chúng ta giả sử token còn hợp lệ và lấy user info từ localStorage
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        try {
          this.currentUser = JSON.parse(userInfo);
          console.log('User info restored successfully:', this.currentUser);
        } catch (error) {
          console.error('Failed to parse user info:', error);
          this.clearTokens();
        }
      } else {
        // Nếu có token nhưng không có userInfo, có thể là dữ liệu cũ
        // Xóa token để buộc user đăng nhập lại
        console.warn('Access token found but no user info, clearing tokens');
        this.clearTokens();
      }
    } else if (accessToken && this.isAccessTokenExpired()) {
      console.log('Access token expired, attempting to refresh...');
      // Token đã hết hạn, thử refresh
      this.refreshAccessToken().catch(() => {
        console.log('Token refresh failed, clearing tokens');
        this.clearTokens();
      });
    } else {
      console.log('No valid access token found');
    }
  }

  /**
   * Lưu tokens vào localStorage
   */
  private saveTokens(accessToken: string, refreshToken: string, accessTokenExpiresAt: string, refreshTokenExpiresAt: string): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('accessTokenExpiresAt', accessTokenExpiresAt);
    localStorage.setItem('refreshTokenExpiresAt', refreshTokenExpiresAt);
    // Lưu user info ngay lập tức nếu có
    if (this.currentUser) {
      localStorage.setItem('userInfo', JSON.stringify(this.currentUser));
    }
  }

  /**
   * Xóa tất cả tokens
   */
  private clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('accessTokenExpiresAt');
    localStorage.removeItem('refreshTokenExpiresAt');
    localStorage.removeItem('userInfo');
  }

  /**
   * Thông báo thay đổi auth state
   */
  private notifyAuthChange(): void {
    this.authChangeListeners.forEach(callback => callback(this.currentUser));
  }
}

// Export singleton instance
export const authService = AuthService.getInstance(); 